import {
  AgenticApplicantFilter,
  AgenticApplicantsResponse,
  AgenticAssessment,
  AgenticAvatarConnection,
  AgenticCandidateSession,
  AgenticConfig,
  AgenticDraft,
  AgenticInviteResponse,
  AgenticMySession,
  AgenticRecordingUrls,
  AgenticReport,
  AgenticResultRow,
} from '../types/agentic_assessment_types';
import { rtkApi } from './rtkApi';

const base = '/agentic-assessment';

const api = rtkApi.enhanceEndpoints({
  addTagTypes: ['AgenticAssessment', 'AgenticApplicants', 'AgenticResults', 'AgenticSession'],
});

export const agenticAssessmentApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // --- recruiter: setup ---
    getAgenticAssessment: builder.query<AgenticAssessment, string>({
      query: (jobOpeningId) => `${base}/${jobOpeningId}`,
      providesTags: (_r, _e, id) => [{ type: 'AgenticAssessment', id }],
    }),
    generateAgenticBlueprint: builder.mutation<
      AgenticAssessment,
      { jobOpeningId: string; jobDescription: string; recruiterNotes?: string; config: AgenticConfig }
    >({
      query: ({ jobOpeningId, ...body }) => ({ url: `${base}/${jobOpeningId}/generate`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { jobOpeningId }) => [{ type: 'AgenticAssessment', id: jobOpeningId }],
    }),
    saveAgenticDraft: builder.mutation<
      AgenticAssessment,
      { jobOpeningId: string; draft: AgenticDraft; config: AgenticConfig }
    >({
      query: ({ jobOpeningId, ...body }) => ({ url: `${base}/${jobOpeningId}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { jobOpeningId }) => [{ type: 'AgenticAssessment', id: jobOpeningId }],
    }),
    approveAgenticBlueprint: builder.mutation<AgenticAssessment, string>({
      query: (jobOpeningId) => ({ url: `${base}/${jobOpeningId}/approve`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'AgenticAssessment', id }],
    }),
    resetAgenticAssessment: builder.mutation<AgenticAssessment, string>({
      query: (jobOpeningId) => ({ url: `${base}/${jobOpeningId}/reset`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'AgenticAssessment', id }],
    }),

    // --- recruiter: candidates and results ---
    getAgenticApplicants: builder.query<
      AgenticApplicantsResponse,
      { jobOpeningId: string; page: number; limit: number; search?: string; status?: AgenticApplicantFilter | '' }
    >({
      query: ({ jobOpeningId, page, limit, search, status }) => ({
        url: `${base}/${jobOpeningId}/applicants`,
        params: {
          page,
          limit,
          ...(search ? { search } : {}),
          ...(status ? { assessment_status: status } : {}),
        },
      }),
      providesTags: (_r, _e, { jobOpeningId }) => [{ type: 'AgenticApplicants', id: jobOpeningId }],
    }),
    inviteAgenticApplicants: builder.mutation<AgenticInviteResponse, { jobOpeningId: string; applicantIds: string[] }>({
      query: ({ jobOpeningId, applicantIds }) => ({
        url: `${base}/${jobOpeningId}/invite`,
        method: 'POST',
        body: { applicantIds },
      }),
      invalidatesTags: (_r, _e, { jobOpeningId }) => [
        { type: 'AgenticApplicants', id: jobOpeningId },
        { type: 'AgenticAssessment', id: jobOpeningId },
      ],
    }),
    getAgenticResults: builder.query<AgenticResultRow[], string>({
      query: (jobOpeningId) => `${base}/${jobOpeningId}/results`,
      providesTags: (_r, _e, id) => [{ type: 'AgenticResults', id }],
    }),
    getAgenticReport: builder.query<AgenticReport, string>({
      query: (sessionId) => `${base}/sessions/${sessionId}/report`,
      providesTags: (_r, _e, id) => [{ type: 'AgenticSession', id }],
    }),
    retryAgenticNarrative: builder.mutation<{ queued: boolean }, string>({
      query: (sessionId) => ({ url: `${base}/sessions/${sessionId}/narrative/retry`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'AgenticSession', id }],
    }),

    // --- candidate ---
    getMyAgenticSessions: builder.query<AgenticMySession[], void>({
      query: () => `${base}/me/sessions`,
    }),
    getAgenticCandidateSession: builder.query<AgenticCandidateSession, string>({
      query: (sessionId) => `${base}/sessions/${sessionId}/candidate`,
      providesTags: (_r, _e, id) => [{ type: 'AgenticSession', id }],
    }),
    startAgenticSession: builder.mutation<AgenticCandidateSession, string>({
      query: (sessionId) => ({ url: `${base}/sessions/${sessionId}/start`, method: 'POST' }),
    }),
    getAgenticRecordingUrls: builder.mutation<AgenticRecordingUrls, { sessionId: string; number: number }>({
      query: ({ sessionId, ...body }) => ({ url: `${base}/sessions/${sessionId}/recording-urls`, method: 'POST', body }),
    }),
    getAgenticAvatar: builder.mutation<AgenticAvatarConnection, string>({
      query: (sessionId) => ({ url: `${base}/sessions/${sessionId}/avatar`, method: 'POST' }),
    }),
    answerAgenticQuestion: builder.mutation<
      AgenticCandidateSession,
      { sessionId: string; number: number; audioKey: string; videoKey?: string }
    >({
      query: ({ sessionId, ...body }) => ({ url: `${base}/sessions/${sessionId}/answer`, method: 'POST', body }),
    }),
    attachAgenticVideo: builder.mutation<{ attached: boolean }, { sessionId: string; number: number; videoKey: string }>({
      query: ({ sessionId, ...body }) => ({ url: `${base}/sessions/${sessionId}/attach-video`, method: 'POST', body }),
    }),
    retryAgenticTurn: builder.mutation<AgenticCandidateSession, { sessionId: string; number: number }>({
      query: ({ sessionId, ...body }) => ({ url: `${base}/sessions/${sessionId}/retry`, method: 'POST', body }),
    }),
    endAgenticSession: builder.mutation<AgenticCandidateSession, string>({
      query: (sessionId) => ({ url: `${base}/sessions/${sessionId}/end`, method: 'POST' }),
    }),
  }),
});

export const {
  useGetAgenticAssessmentQuery,
  useGenerateAgenticBlueprintMutation,
  useSaveAgenticDraftMutation,
  useApproveAgenticBlueprintMutation,
  useResetAgenticAssessmentMutation,
  useGetAgenticApplicantsQuery,
  useInviteAgenticApplicantsMutation,
  useGetAgenticResultsQuery,
  useGetAgenticReportQuery,
  useRetryAgenticNarrativeMutation,
  useGetMyAgenticSessionsQuery,
  useGetAgenticCandidateSessionQuery,
  useStartAgenticSessionMutation,
  useGetAgenticRecordingUrlsMutation,
  useGetAgenticAvatarMutation,
  useAnswerAgenticQuestionMutation,
  useAttachAgenticVideoMutation,
  useRetryAgenticTurnMutation,
  useEndAgenticSessionMutation,
} = agenticAssessmentApi;

/** The backend's `{success: false, error}` message, or a fallback. */
export const agenticErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { data?: { error?: string; detail?: unknown } } | undefined)?.data;
  if (data && typeof data.error === 'string') return data.error;
  if (data && typeof data.detail === 'string') return data.detail;
  return fallback;
};
